/**
 * AddMembersModal Component
 *
 * Modal for adding new members to an existing group conversation.
 * Filters out users who are already in the group.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  UserPlus,
  Check,
  Loader2,
  Users,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useConnections,
  useConnectionActions,
  useConnectionError,
  useConnectionLoading,
} from '@/stores/connection';
import { useMessagingActions } from '@/stores/messaging';
import { getErrorMessageOrFallback } from '@/utils/errors';
import { logger } from '@/utils/logger';
import type { UserConnection } from '@/types/connections';
import type { Conversation } from '@/types/messaging';

interface AddMembersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: Conversation;
}

const MAX_GROUP_SIZE = 50;

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * SelectableConnectionCard
 */
interface SelectableConnectionCardProps {
  connection: UserConnection;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const SelectableConnectionCard: React.FC<SelectableConnectionCardProps> = ({
  connection,
  isSelected,
  onToggle,
  disabled,
}) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`w-full p-3 rounded-lg border transition-all duration-200 text-left ${
        isSelected
          ? 'border-primary bg-primary/5'
          : disabled
          ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
          : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar className="h-9 w-9">
            <AvatarImage src={connection.user.avatar} alt={displayName} />
            <AvatarFallback
              style={{
                backgroundColor: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
              }}
              className="text-xs font-semibold"
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {connection.user.isOnline && (
            <span
              className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card"
              style={{ backgroundColor: 'hsl(var(--primary))' }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          <p className="text-xs text-gray-500">@{connection.user.username}</p>
        </div>

        <div
          className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? 'border-primary bg-primary'
              : 'border-gray-300 bg-white'
          }`}
        >
          {isSelected && <Check className="h-3 w-3 text-white" />}
        </div>
      </div>
    </button>
  );
};

export const AddMembersModal: React.FC<AddMembersModalProps> = ({
  open,
  onOpenChange,
  conversation,
}) => {
  const connections = useConnections();
  const { fetchConnections } = useConnectionActions();
  const { isLoading } = useConnectionLoading();
  const connectionError = useConnectionError();
  const { addMembersToGroup } = useMessagingActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get IDs of users already in the group
  const existingMemberIds = useMemo(
    () => new Set(conversation.participants?.map((p) => p.id) || []),
    [conversation.participants]
  );

  const currentMemberCount = existingMemberIds.size;
  const remainingSlots = MAX_GROUP_SIZE - currentMemberCount;

  // Fetch connections when modal opens so this flow does not depend on
  // another page or modal having warmed the connection store.
  useEffect(() => {
    if (open) {
      void fetchConnections(true).catch((fetchError) => {
        logger.warn('Add members connection fetch failed', {
          error: getErrorMessageOrFallback(
            fetchError,
            'Failed to load connections'
          ),
        });
      });
    }
  }, [open, fetchConnections]);

  // Filter connections: only show those not already in group
  const availableConnections = useMemo(() => {
    return connections.filter((c) => !existingMemberIds.has(c.user.id));
  }, [connections, existingMemberIds]);

  // Filter by search query
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return availableConnections;

    const query = searchQuery.toLowerCase().trim();
    return availableConnections.filter((c) => {
      const name = `${c.user.firstName} ${c.user.lastName}`.toLowerCase();
      const username = c.user.username.toLowerCase();
      return name.includes(query) || username.includes(query);
    });
  }, [availableConnections, searchQuery]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedUserIds(new Set());
      setError(null);
    }
  }, [open]);

  /**
   * Toggle user selection
   */
  const handleToggleUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        // Check if we can add more
        if (newSet.size >= remainingSlots) {
          return prev;
        }
        newSet.add(userId);
      }
      return newSet;
    });
    setError(null);
  }, [remainingSlots]);

  /**
   * Handle adding members
   */
  const handleAddMembers = useCallback(async () => {
    if (selectedUserIds.size === 0) return;

    setIsAdding(true);
    setError(null);

    try {
      const memberIds = Array.from(selectedUserIds);
      await addMembersToGroup(conversation.id, memberIds);
      onOpenChange(false);
    } catch (err) {
      console.error('[AddMembersModal] Failed to add members:', err);
      setError(err instanceof Error ? err.message : 'Failed to add members');
    } finally {
      setIsAdding(false);
    }
  }, [selectedUserIds, conversation.id, addMembersToGroup, onOpenChange]);

  const canAdd = selectedUserIds.size > 0 && !isAdding;
  const isAtMaxCapacity = selectedUserIds.size >= remainingSlots;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="max-w-md max-h-[70vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'hsl(var(--primary) / 0.1)' }}
            >
              <UserPlus className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
            </div>
            Add Members
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1 ml-12">
            Add connections to "{conversation.groupName}"
          </DialogDescription>
        </DialogHeader>

        {/* Search input */}
        <div className="px-6 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-gray-200 focus:border-primary focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Selection info */}
        {selectedUserIds.size > 0 && (
          <div className="px-6 pb-2">
            <p className="text-sm text-gray-600">
              {selectedUserIds.size} selected
              {isAtMaxCapacity && (
                <span className="text-amber-600 ml-2">(max capacity)</span>
              )}
            </p>
          </div>
        )}

        {/* Connections list */}
        <ScrollArea className="flex-1 min-h-[150px] max-h-[250px]">
          <div className="px-6 pb-4 space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
              </div>
            ) : connectionError ? (
              <div className="text-center py-6">
                <p className="text-sm font-medium mb-1 text-gray-900">
                  Failed to load connections
                </p>
                <p className="text-xs text-gray-500">{connectionError}</p>
              </div>
            ) : filteredConnections.length === 0 ? (
              <div className="text-center py-6">
                <div
                  className="h-10 w-10 rounded-full mx-auto mb-2 flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                >
                  <Users className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
                </div>
                <p className="text-sm font-medium mb-1 text-gray-900">
                  {searchQuery
                    ? 'No connections found'
                    : availableConnections.length === 0
                    ? 'All connections are already in this group'
                    : 'No connections available'}
                </p>
                <p className="text-xs text-gray-500">
                  {searchQuery
                    ? 'Try a different search term'
                    : 'Connect with more users to add them'}
                </p>
              </div>
            ) : (
              filteredConnections.map((connection) => {
                const isSelected = selectedUserIds.has(connection.user.id);
                const isDisabled = !isSelected && isAtMaxCapacity;
                return (
                  <SelectableConnectionCard
                    key={connection.connectionId}
                    connection={connection}
                    isSelected={isSelected}
                    onToggle={() => handleToggleUser(connection.user.id)}
                    disabled={isDisabled}
                  />
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Error message */}
        {error && (
          <div className="px-6 pb-2">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 pt-4 border-t bg-gray-50 flex justify-between items-center">
          <span className="text-xs text-gray-500">
            {remainingSlots - selectedUserIds.size} slot{remainingSlots - selectedUserIds.size !== 1 ? 's' : ''} remaining
          </span>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={!canAdd}
              style={
                canAdd
                  ? {
                      backgroundColor: 'hsl(var(--primary))',
                      color: 'hsl(var(--primary-foreground))',
                    }
                  : undefined
              }
            >
              {isAdding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add {selectedUserIds.size > 0 ? `(${selectedUserIds.size})` : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddMembersModal;
